import * as React from 'react';

import createTrackingFunctions from './createTrackingFunctions';

import { TrackingFunctions, TrackingMetadata } from './types';

/** Placeholder trackEvent to feed into defaultContext. This should never be used. */
const defaultTrackEvent = (/* eventName, metaData */) => Promise.resolve();
const defaultContext = createTrackingFunctions(defaultTrackEvent);

export const TrackingContext = React.createContext<TrackingFunctions>(
  defaultContext
);

export const useTrackingContext = (): TrackingFunctions => {
  return React.useContext(TrackingContext);
};

type Props = {
  children: React.ReactNode;
  trackingFunctions?: TrackingFunctions;
  metadata?: TrackingMetadata;
};

export const TrackingProvider = ({
  children,
  trackingFunctions: propsTrackingFunctions,
  metadata,
}: Props) => {
  let trackingFunctions: TrackingFunctions = useTrackingContext();

  // Context TrackingFunctions may be overridden by props.trackingFunctions
  if (propsTrackingFunctions) {
    trackingFunctions = propsTrackingFunctions;
  }

  /*
   * If metadata is provided, enhance TrackingFunctions with it.
   * Metadata is enhanced for descendants within context boundaries.
   * TrackingProvider may live within boundaries of other TrackingProvider
   * to allow for progressive enhancement of metadata down the component hierarchy.
   */
  if (metadata) {
    const { trackEvent } = trackingFunctions;
    trackingFunctions = createTrackingFunctions((eventName, eventData) =>
      trackEvent(eventName, { ...metadata, ...eventData })
    );
  }

  return (
    <TrackingContext.Provider value={trackingFunctions}>
      {children}
    </TrackingContext.Provider>
  );
};
